import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, ViewChild, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { CustomDateAdapter } from '@shared/services/custom-date-adapter';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { CriarCompraOcorrenciaRequest } from '@shared/services/requests/compras/criar-compra-ocorrencia.request';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { CampoNativoEnum } from '@shared/enums/compras/campo-nativo.enum';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { CompraAprovacaoService } from '@shared/services/apis/compras/compra-aprovacao.service';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { OrquestradorCompraEtapaService } from '@shared/services/apis/orquestrador-compras/compra-etapa.service';
import { AutomacaoService } from '@shared/services/apis/compras/automacao.service';
import { CompraPedidoService } from '@shared/services/apis/compras/compra-pedido.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { TipoAutomacaoEnum } from '@shared/enums/compras/tipo-automacao.enum';
import { TipoPrioridadeEnum } from '@shared/enums/compras/tipo-prioridade.enum';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import { ControleEstoqueService } from '@shared/services/apis/produto/controle-estoque.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { AtualizarCompraRequest } from '@shared/services/requests/compras/atualizar-compra.request';
import { AtualizarTituloCompraRequest } from '@shared/services/requests/compras/atualizar-titulo-compra.request';
import { DetalheCompraResponse } from '@shared/services/responses/compras/detalhe-compra.response';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { CompraOcorrenciaStruct } from '@shared/services/structs/compras/compra-ocorrencia.struct';
import { KanbanCard, KanbanStage, Person } from '../../processo-compras.models';
import {
  avatarColorFromSeed,
  CardAttachment,
  CardComment,
  CardDynamicField,
  CardEditDialogData,
  CardHistoryItem,
  CardNotaContexto,
  CardNotaPedido,
  CardProduct,
  dataDaApi,
  formatBytes,
  fmtMoneyDisplay,
  HistoryCompraLink,
  kindFromName,
  montarHistorico,
  montarPedidoState,
  MoveForward,
  parseBR,
  PedidoItemView,
  PedidoState,
  rotuloNotaFiscal,
  SIDE_STAGE_KEYS,
  STAGE_META,
  ETAPAS_FINALIZACAO_SEM_NF,
  toApiDate,
} from './card-edit-dialog.types';
import { CedAnexosComponent } from './panels/ced-anexos.component';
import { CedComentariosComponent } from './panels/ced-comentarios.component';
import { CedHistoricoComponent } from './panels/ced-historico.component';
import { CedSplitComponent } from './panels/ced-split.component';
import { CedStageAprovacaoComponent } from './stages/ced-stage-aprovacao.component';
import { CedStageCanceladoComponent } from './stages/ced-stage-cancelado.component';
import { CedStageConferenciaComponent } from './stages/ced-stage-conferencia.component';
import { CedStageCotacaoComponent } from './stages/ced-stage-cotacao.component';
import { CedStageDivergenciaComponent, DesfechoDivergenciaEnum } from './stages/ced-stage-divergencia.component';
import { CedStageFinalizadoComponent } from './stages/ced-stage-finalizado.component';
import { EnviarPedidoPayload } from './stages/ced-envio-pedido.component';
import { CedStagePedidoComponent } from './stages/ced-stage-pedido.component';
import { CedStageNotaComponent } from './stages/ced-stage-nota.component';
import { CedNotaItensComponent, NotaItensCarregados } from './stages/ced-nota-itens.component';
import { NotaFiscalEntradaContextoVinculoPedidoResponse } from '@shared/services/responses/orquestrador-compras/nota-fiscal-entrada-contexto-vinculo-pedido.response';
import { CedStageSolicitacaoComponent } from './stages/ced-stage-solicitacao.component';

// Tipos e constantes compartilhados do dialog vivem em card-edit-dialog.types.ts.
export * from './card-edit-dialog.types';

interface VinculoOpcao {
  id: number;
  descricao: string;
}

/**
 * Shell do diálogo de edição do card: cabeçalho, coluna esquerda (composição dos painéis),
 * movimentação de etapa, salvamento (doSave), confirms de fechar/cancelar, responsável e
 * campos dinâmicos da etapa. As visões de cada etapa vivem em stages/ (componentes filhos).
 */
@Component({
  selector: 'app-card-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AbntPipe,
    SelectPesquisavelComponent,
    AlertCardComponent,
    CedAnexosComponent,
    CedComentariosComponent,
    CedHistoricoComponent,
    CedSplitComponent,
    CedStageAprovacaoComponent,
    CedStageCanceladoComponent,
    CedStageConferenciaComponent,
    CedStageCotacaoComponent,
    CedStageDivergenciaComponent,
    CedStageFinalizadoComponent,
    CedStagePedidoComponent,
    CedStageNotaComponent,
    CedNotaItensComponent,
    CedStageSolicitacaoComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './card-edit-dialog.component.html',
  styleUrls: ['./ced-scrollbars.css', './card-edit-dialog.component.css'],
})
export class CardEditDialogComponent implements OnDestroy {
  card: KanbanCard;
  allStages: KanbanStage[];
  transitions: Record<string, string[]>;

  products: CardProduct[] = [];
  camposEtapa: CardDynamicField[] = [];
  private readonly vinculoControls = new Map<number, FormControl<number | null>>();
  private readonly vinculoPreloads = new Map<number, VinculoOpcao[]>();
  campoNumeroMask: ReturnType<MaskService['getDecimalMask2']>;
  campoMoedaMask: ReturnType<MaskService['getDynamicCurrencyMask']>;
  private _history: CardHistoryItem[] = [];
  /** Ocorrências da compra (histórico), lidas também pelas etapas Cancelado e Finalizado. */
  ocorrencias: CompraOcorrenciaStruct[] = [];
  isCarregandoHistorico = false;
  isRegistrandoAnotacao = false;
  @ViewChild('historicoTimeline') private historicoTimeline?: CedHistoricoComponent;

  peopleList: { key: string; name: string; color: string; idUsuario: number }[] = [];
  responsavelControl = new FormControl<number | null>(null);

  moveOpen = false;

  // Anexos / Comentários — os painéis (upload, composer) vivem nos filhos;
  // o shell mantém os arrays para os badges de contagem dos chips.
  panel: 'anexos' | 'comentarios' | null = null;
  attachments: CardAttachment[] = [];
  comments: CardComment[] = [];

  dataEntrega: Date | null = null;
  setor = '';
  /** Pedido sem NF-e (editável em Pedido, Aprovação e Faturado); com a marca, o motivo é obrigatório. Enviado no doSave. */
  isSemNotaFiscal = false;
  motivoSemNotaFiscal = '';
  isDirty = false;
  saving = false;
  cancelando = false;
  /** Edição inline do título (própria compra) — salva no endpoint dedicado, fora do doSave. */
  tituloEditando = false;
  tituloDraft = '';
  /** Marca que o título foi salvo separadamente do doSave, para o `cancel()` ainda devolver o card atualizado. */
  private tituloAlterado = false;
  /** Conferência física gravou algo (contagem, conclusão ou problema): o fechamento devolve 'saved' para o quadro recarregar. */
  conferenciaAlterada = false;
  /** Painel do Finalizado lançou ou excluiu estoque ou financeiro: o fechamento devolve 'saved' para o quadro recarregar. */
  lancamentoAlterado = false;
  /**
   * Aprovação revogada fora da etapa de Aprovação (Faturado, Conferência, Divergência): o ajuste do vínculo NF × pedido
   * mudou o total de faixa de alçada e o backend cancelou as decisões do ciclo. Exibe o bloco de aprovação acima da etapa.
   */
  aprovacaoRevogadaPendente = false;
  /** Nova aprovação registrada pelo bloco de aprovação revogada: o fechamento devolve 'saved' para o quadro recarregar. */
  private reaprovacaoRegistrada = false;
  /**
   * Conferência cega não concluída, informada pelo ced-stage-conferencia a cada carga da situação. Começa `true`
   * para as quantidades não aparecerem antes da situação chegar; em falha técnica o filho emite `false`, e com a
   * situação recusada por regra de negócio não emite (segue oculto).
   */
  conferenciaCegaPendente = true;
  /** Espelho da justificativa editada na etapa de solicitação — enviada no doSave. */
  justificativa = '';
  /**
   * Fornecedor definido para esta compra. Só passa a existir de Pedido em diante: na
   * Cotação a compra ainda pode ter vários candidatos, e no split cada filha nasce com o seu.
   */
  idFornecedor: number | null = null;
  nomeFornecedor = '';
  /**
   * Estado do Pedido de Compra, editado por referência pelo filho ced-stage-pedido.
   * Montado sempre (mesmo fora da etapa de pedido) para que salvar de qualquer etapa
   * preserve (reenvie) esses valores — como setor/justificativa.
   */
  pedidoState: PedidoState;
  /**
   * Id do "Critério da escolha" da cotação (CriterioCotacaoEnum, o mesmo de
   * compra.idCriterioCotacao). Vive no shell (não no @ViewChild, só garantido vivo em
   * card.stage === 'cotacao') para que salvar de qualquer etapa preserve o critério já escolhido.
   */
  cotCriterioIndex: number | null = null;

  cotJustificativa = '';
  /** Espelho da observação de encerramento (ced-stage-finalizado) — reenviada no doSave. */
  finalizadoObservacao = '';
  // ids dos campos nativos marcados como obrigatórios na etapa atual (etapa_kanban_campo_nativo).
  private nativosObrigatorios = new Set<number>();
  currentPerson: Person | undefined;

  selectedHistoryItem: CardHistoryItem | null = null;
  isExpanded = false;

  /**
   * Modo NF: do Faturado em diante (exceto Cancelado), pedido com NF vinculada abre sempre pela nota. A NF vem
   * de quem abriu (moldura do quadro, troca de pedido/NF) ou é a de vínculo mais recente do pedido. `null` = fora do modo NF.
   */
  notaModoNf: CardNotaContexto | null = null;
  /** NFs do pedido no modo NF, na ordem dos vínculos: com 2 ou mais, o card troca entre elas. */
  notasPedido: { idNFeEntrada: number; rotulo: string }[] = [];
  /** Modo NF: pedidos da nota, primeiro os de quem abriu e depois os do contexto carregado. */
  pedidosNota: CardNotaPedido[] = [];
  /** Nota carregada pelo ced-nota-itens, para a seção "Nota fiscal" da coluna esquerda. */
  notaCarregada: NotaItensCarregados | null = null;
  /**
   * Até a primeira emissão do ced-nota-itens (sucesso, falha ou sem nota): skeleton na seção "Nota fiscal".
   * Signal (como carregandoEtapa): os filhos emitem durante a checagem do template (ced-nota-itens com o contexto já
   * carregado, ced-stage-finalizado no ngOnInit), e a escrita no signal refaz a checagem em vez de gerar NG0100.
   */
  readonly carregandoNota = signal(true);
  /** Etapa com carga assíncrona (Finalizado: situação dos lançamentos): o loader de página cobre o corpo. */
  readonly carregandoEtapa = signal(false);
  /**
   * Contexto da nota do modo NF vindo do bloco da NF da etapa (Faturado, Conferência, Divergência), para a
   * tabela e o painel da conferência não buscarem de novo. `undefined` = aguardando o bloco; `null` = etapa sem
   * o bloco ou falha: cada um busca sozinho.
   */
  contextoNotaModoNf: NotaFiscalEntradaContextoVinculoPedidoResponse | null | undefined;

  /** Filho da etapa de cotação — vivo sempre que card.stage === 'cotacao' (@switch). */
  @ViewChild(CedStageCotacaoComponent) private cotacaoStage?: CedStageCotacaoComponent;
  /** Bloco da NF da etapa (Faturado, Conferência, Divergência): a Divergência reabre o vínculo por ele. */
  @ViewChild(CedStageNotaComponent) private notaStage?: CedStageNotaComponent;
  @ViewChild('tituloInput') private tituloInput?: ElementRef<HTMLInputElement>;

  private readonly subs = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<CardEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CardEditDialogData,
    private compraService: CompraService,
    private compraAprovacaoService: CompraAprovacaoService,
    private orquestradorCompraEtapaService: OrquestradorCompraEtapaService,
    private compraPedidoService: CompraPedidoService,
    private automacaoService: AutomacaoService,
    private controleEstoqueService: ControleEstoqueService,
    private authService: AuthService,
    private alertService: AlertService,
    private maskService: MaskService,
    private pessoaLookupService: PessoaLookupService,
    private notaFiscalEntradaService: NotaFiscalEntradaService,
    private dialog: MatDialog,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    this.campoNumeroMask = this.maskService.getDecimalMask2();
    this.campoMoedaMask = this.maskService.getDynamicCurrencyMask();

    this.card = { ...data.card };
    this.allStages = data.allStages;
    this.transitions = data.transitions;
    this.nativosObrigatorios = new Set(data.nativosObrigatorios ?? []);
    this.iniciarModoNf(data.detalhe);
    // Mesmas etapas que exibem o ced-stage-nota no template.
    if (!['faturado', 'conferencia', 'divergencia'].includes(this.card.stage)) this.contextoNotaModoNf = null;

    const d = data.detalhe;
    const usersById = data.usersById;

    if (d) {
      if (d.compra?.dataEntregaDesejada) {
        this.dataEntrega = dataDaApi(d.compra.dataEntregaDesejada);
      }
      this.setor = d.compra?.setor || '';
      this.isSemNotaFiscal = !!d.compra?.isSemNotaFiscal;
      this.motivoSemNotaFiscal = d.compra?.motivoSemNotaFiscal || '';
      this.justificativa = d.compra?.justificativa || '';
      this.idFornecedor = d.compra?.idFornecedor ?? null;
      this.carregarNomeFornecedor();
      this.cotCriterioIndex = d.compra?.idCriterioCotacao ?? null;
      this.cotJustificativa = d.compra?.justificativaCotacao || '';

      // Products
      this.products = (d.itens || []).map(i => ({
        idCompraItem: i.idCompraItem,
        idProduto: i.idProduto ?? null,
        sku: i.codigoSku || '',
        name: i.nome,
        marca: i.marca ?? null,
        qty: i.quantidade,
        unit: i.unidade,
        stock: null,
        price: fmtMoneyDisplay(i.valorUnitario),
        valorUnitario: i.valorUnitario ?? null,
        isAvulso: i.isAvulso,
        observacao: i.observacao ?? null,
      }));

      // Attachments
      this.attachments = (d.anexos || []).map(a => ({
        idAnexo: a.idCompraAnexo,
        name: a.nomeArquivo,
        size: formatBytes(a.tamanhoBytes),
        kind: kindFromName(a.nomeArquivo),
        who: usersById?.get(a.idUsuario)?.initials || '',
        whoName: usersById?.get(a.idUsuario)?.name || String(a.idUsuario),
        whoColor: usersById?.get(a.idUsuario)?.color || avatarColorFromSeed(String(a.idUsuario)),
        when: new Date(a.datetimeInclusao).toLocaleDateString('pt-BR'),
        url: a.urlArquivo,
      }));

      // Comments
      this.comments = (d.comentarios || []).map(c => {
        const p = usersById?.get(c.idUsuario);
        return {
          who: p?.initials || '',
          whoName: p?.name || String(c.idUsuario),
          whoColor: p?.color || avatarColorFromSeed(p?.initials || String(c.idUsuario)),
          when: new Date(c.datetimeInclusao).toLocaleDateString('pt-BR'),
          text: c.texto,
        };
      });

      this.camposEtapa = (d.campos || []).map(c => ({
        ...c,
        valor: c.valor ?? null,
        idCampoDefinicaoOpcao: c.idCampoDefinicaoOpcao ?? null,
        valorData: c.idTipoCampo === 4 && c.valor ? new Date(c.valor) : null,
        valorMultiselect: c.idTipoCampo === 6 ? this.parseMultiselectValue(c.valor) : [],
        valorNumero: (c.idTipoCampo === 2 || c.idTipoCampo === 3) ? this.parseNumeroValue(c.valor) : null,
      }));
    }

    this.carregarHistorico(true);

    // Carrega sempre os dados de pedido/finalizado a partir do detalhe, para que salvar
    // a partir de qualquer etapa preserve (reenvie) esses valores — como setor/justificativa.
    this.pedidoState = montarPedidoState(d, this.products);
    this.finalizadoObservacao = d?.compra?.observacaoEncerramento || '';
    this.hidratarEstoquesProdutosSalvos();

    if (usersById) {
      this.peopleList = Array.from(usersById.entries()).map(([, p]) => ({
        key: p.initials,
        name: p.name,
        color: p.color,
        idUsuario: p.idUsuario,
      }));
      const me = this.authService.getUserInfos();
      this.currentPerson = usersById.get(me.idUsuario);
    }

    if (this.card.resp && !this.peopleList.some(p => p.idUsuario === this.card.idUsuarioResponsavel)) {
      this.peopleList = [{
        key: this.card.resp,
        name: this.card.respName || this.card.resp,
        color: this.card.respColor || avatarColorFromSeed(this.card.resp),
        idUsuario: this.card.idUsuarioResponsavel ?? 0,
      }, ...this.peopleList];
    }
    this.responsavelControl.setValue(this.card.idUsuarioResponsavel ?? null, { emitEvent: false });
    if (this.isSomenteLeitura) {
      this.responsavelControl.disable({ emitEvent: false });
    }
    this.carregarAprovacaoRevogada();
  }

  /**
   * Só nas etapas pós-pedido em que o ajuste do vínculo pode revogar a aprovação; no fluxo normal
   * (Solicitação a Aprovação) e nas etapas finais não há consulta.
   */
  private carregarAprovacaoRevogada(): void {
    if (!this.card.idCompra || this.isArquivada) return;
    if (!['faturado', 'conferencia', 'divergencia'].includes(this.card.stage)) return;
    this.subs.add(this.compraAprovacaoService.getAprovacao(this.card.idCompra).subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar a aprovação.', AlertType.error);
          return;
        }
        this.aprovacaoRevogadaPendente = res.statusGeral === 'pendente';
      },
      error: () => this.alertService.show('Erro', 'Não foi possível carregar a aprovação.', AlertType.error),
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('card-edit-panel--expanded');
      return;
    }

    this.dialogRef.updateSize('', '');
    this.dialogRef.removePanelClass('card-edit-panel--expanded');
  }

  onResponsavelChange(person: { key: string; name: string; color: string; idUsuario: number } | number | null): void {
    if (person === null || person === undefined) return;
    const selected = typeof person === 'number'
      ? this.peopleList.find(p => p.idUsuario === person)
      : person;
    if (!selected) return;
    if (selected.idUsuario === (this.card.idUsuarioResponsavel ?? null)) return;
    this.card.resp = selected.key;
    this.card.respName = selected.name || selected.key;
    this.card.respColor = selected.color || avatarColorFromSeed(selected.key);
    this.card.idUsuarioResponsavel = selected.idUsuario;
    this.isDirty = true;
  }

  // ----- Título (edição inline, endpoint próprio) -----
  iniciarEdicaoTitulo(): void {
    if (this.isSomenteLeitura) return;
    this.tituloDraft = this.card.title || '';
    this.tituloEditando = true;
    setTimeout(() => this.tituloInput?.nativeElement.focus());
  }

  cancelarTitulo(): void {
    this.tituloEditando = false;
  }

  salvarTitulo(): void {
    if (!this.tituloEditando) return;
    const novoTitulo = this.tituloDraft.trim();
    const tituloAnterior = this.card.title || '';
    this.tituloEditando = false;

    if (novoTitulo === tituloAnterior || !this.card.idCompra) {
      return;
    }

    const request: AtualizarTituloCompraRequest = { titulo: novoTitulo || null };
    this.card.title = novoTitulo;

    this.compraService.atualizarTitulo(this.card.idCompra, request).subscribe({
      next: (res) => {
        if (res.isError) {
          this.card.title = tituloAnterior;
          this.alertService.show('Atenção', res.errorDescription || 'Não foi possível salvar o título.', AlertType.warning);
          return;
        }
        this.tituloAlterado = true;
      },
      error: () => {
        this.card.title = tituloAnterior;
        this.alertService.show('Erro', 'Não foi possível salvar o título.', AlertType.error);
      },
    });
  }

  get stage(): KanbanStage {
    return this.allStages.find(s => s.key === this.card.stage) || this.allStages[0];
  }

  // Campo nativo 5 = Justificativa. Obrigatoriedade pré-carregada pela página.
  get justificativaObrigatoria(): boolean {
    return this.nativosObrigatorios.has(5);
  }

  get meta() {
    const base = STAGE_META[this.card.stage];
    const mainKeys = this.mainFlowKeys;
    const idx = mainKeys.indexOf(this.card.stage);
    const isTerminal = (this.transitions[this.card.stage] || []).length === 0;
    const nota = base?.stepNote ?? (isTerminal ? 'concluída' : 'ativa');
    const stepLabel = idx >= 0 ? `Etapa ${idx + 1} de ${mainKeys.length} · ${nota}` : '';
    return {
      stepLabel: base?.stageLabel ?? stepLabel,
      instruction: base?.instruction ?? (isTerminal
        ? 'Esta etapa está concluída.'
        : 'Avance para a próxima etapa ou retorne conforme necessário.'),
    };
  }

  private get mainFlowKeys(): string[] {
    return this.allStages
      .filter(s => !(s.isEspecial ?? SIDE_STAGE_KEYS.has(s.key)))
      .map(s => s.key);
  }

  get history(): CardHistoryItem[] {
    return this._history;
  }

  openHistoryDetail(h: CardHistoryItem): void {
    this.selectedHistoryItem = h;
  }

  closeHistoryDetail(): void {
    this.selectedHistoryItem = null;
  }

  /**
   * Compra citada no histórico (cotação de origem × pedido gerado): fecha este card e devolve o
   * id para a página abrir o outro — sem card sobre card, e alcançando também a cotação
   * arquivada, que não está no quadro.
   */
  abrirCompraDoHistorico(compra: HistoryCompraLink): void {
    if (compra.idCompra === this.card.idCompra) return;
    if (this.isDirty) {
      this.alertService.show('Atenção',
        'Salve as alterações antes de abrir outro card.', AlertType.warning);
      return;
    }
    this.dialogRef.close({ card: this.card, action: 'abrir-compra', idCompra: compra.idCompra });
  }

  /** Bloco da NF carregou (ou recarregou após vínculo): a tabela do modo NF usa o contexto da mesma nota. */
  onContextosNotaCarregados(contextos: NotaFiscalEntradaContextoVinculoPedidoResponse[]): void {
    const nota = this.notaModoNf;
    if (!nota) return;
    this.contextoNotaModoNf = contextos.find(c => c.nota?.idNFeEntrada === nota.idNFeEntrada) ?? null;
  }

  onNotaCarregada(carregados: NotaItensCarregados | null): void {
    this.carregandoNota.set(false);
    this.notaCarregada = carregados;
    if (carregados?.pedidos.length) this.pedidosNota = carregados.pedidos;
  }

  /** Modo NF: troca para outro pedido da nota — fecha e o quadro reabre o card dele, ainda no modo NF. */
  trocarPedidoNota(idCompra: number): void {
    if (!this.notaModoNf || idCompra === this.card.idCompra) return;
    if (this.isDirty) {
      this.alertService.show('Atenção',
        'Salve as alterações antes de abrir outro card.', AlertType.warning);
      return;
    }
    const nota: CardNotaContexto = { ...this.notaModoNf, pedidos: this.pedidosNota };
    this.dialogRef.close({ card: this.card, action: 'abrir-pedido-nota', idCompra, nota });
  }

  /** Modo NF com 2+ NFs no pedido: troca de nota — fecha e o quadro reabre o mesmo pedido pela outra NF. */
  trocarNotaPedido(idNFeEntrada: number): void {
    if (!this.notaModoNf || idNFeEntrada === this.notaModoNf.idNFeEntrada) return;
    if (this.isDirty) {
      this.alertService.show('Atenção',
        'Salve as alterações antes de trocar de nota.', AlertType.warning);
      return;
    }
    const nota: CardNotaContexto = {
      idNFeEntrada,
      rotulo: this.notasPedido.find(n => n.idNFeEntrada === idNFeEntrada)?.rotulo ?? 'NF-e',
      pedidos: [this.pedidoAtualNota()],
    };
    this.dialogRef.close({ card: this.card, action: 'abrir-pedido-nota', idCompra: this.card.idCompra, nota });
  }

  /**
   * Resolve o modo NF numa abertura qualquer (quadro, moldura, histórico, troca): do Faturado em diante pela
   * ordem de allStages (inclui Conferência, Finalizado e Divergência; Cancelado fica fora, pois cancela de
   * qualquer etapa), pedido com NF vinculada abre pela nota. Sem NF de quem abriu, usa a de vínculo mais recente.
   */
  private iniciarModoNf(d: DetalheCompraResponse | undefined): void {
    const atual = this.allStages.findIndex(s => s.key === this.card.stage);
    const faturado = this.allStages.findIndex(s => s.key === 'faturado');
    const isNfEmDiante = this.card.stage !== 'cancelado' && faturado >= 0 && atual >= faturado;
    const vinculos = [...(d?.vinculosNota ?? [])].sort((a, b) => a.idCompraNFeEntradaVinculo - b.idCompraNFeEntradaVinculo);
    if (!this.card.idCompra || (!this.data.nota && (!isNfEmDiante || !vinculos.length))) return;

    // Fora da moldura só o pedido aberto é conhecido: os demais pedidos da nota chegam com o ced-nota-itens.
    const nota: CardNotaContexto = this.data.nota ?? {
      idNFeEntrada: vinculos[vinculos.length - 1].idNFeEntrada,
      rotulo: 'NF-e',
      pedidos: [this.pedidoAtualNota()],
    };
    this.notaModoNf = nota;
    this.pedidosNota = nota.pedidos;
    // Uma entrada por NF (o Map mantém a posição do primeiro vínculo).
    this.notasPedido = [...new Map(vinculos.map(v => [v.idNFeEntrada, {
      idNFeEntrada: v.idNFeEntrada,
      rotulo: v.idNFeEntrada === nota.idNFeEntrada ? nota.rotulo : 'NF-e',
    }])).values()];
    if (!this.data.nota || this.notasPedido.length > 1) this.carregarRotulosNotas();
  }

  /** O detalhe da compra só traz o id das NFs: número e série vêm da consulta de notas por compra. */
  private carregarRotulosNotas(): void {
    this.subs.add(this.notaFiscalEntradaService.notasPorCompras([this.card.idCompra!]).subscribe({
      next: (res) => {
        if (res.isError) return;
        for (const n of res.lista ?? []) {
          const nota = this.notasPedido.find(x => x.idNFeEntrada === n.idNFeEntrada);
          if (nota) nota.rotulo = rotuloNotaFiscal(n.numero, n.serie);
        }
        const atual = this.notasPedido.find(x => x.idNFeEntrada === this.notaModoNf?.idNFeEntrada);
        if (atual && this.notaModoNf) this.notaModoNf.rotulo = atual.rotulo;
      },
    }));
  }

  /** O pedido aberto como pedido da nota, com o que o card já tem (valor e etapa como no quadro). */
  private pedidoAtualNota(): CardNotaPedido {
    return {
      idCompra: this.card.idCompra ?? 0,
      codigo: this.card.id,
      nomeEtapa: this.stage.name,
      valorLabel: this.card.value === '—' ? '—' : `R$ ${this.card.value}`,
      nomeFornecedor: this.nomeFornecedor,
      corEtapa: this.stage.color,
      iconeEtapa: this.stage.icon,
    };
  }

  /** Reconstrói a timeline a partir das ocorrências e fecha o painel de detalhe (o item selecionado ficaria órfão). */
  private aplicarHistorico(ocorrencias: CompraOcorrenciaStruct[]): void {
    this.selectedHistoryItem = null;
    this.ocorrencias = ocorrencias;
    this._history = montarHistorico(ocorrencias, this.data.usersById);
    this.resolverFornecedoresHistorico();
  }

  /** O diff de fornecedor vem com ids (a API de Compras não guarda nomes): troca pelo nome do cadastro. */
  private resolverFornecedoresHistorico(): void {
    const mudancas = this._history
      .flatMap(h => h.alteracoes ?? [])
      .filter(a => a.campo === 'idFornecedor');
    const ids = [...new Set(mudancas
      .flatMap(a => [a.de, a.para])
      .map(v => Number(v))
      .filter(id => Number.isFinite(id) && id > 0))];
    if (!ids.length) return;

    this.subs.add(this.pessoaLookupService.getListPessoaIds({ listIdPessoa: ids }).subscribe({
      next: (res) => {
        if (res.isError) return;
        const nomes = new Map((res.listaPessoa || []).map(p => [String(p.idPessoa), p.nome || p.nomeFantasia || '']));
        for (const a of mudancas) {
          a.de = (a.de && nomes.get(a.de)) || a.de;
          a.para = (a.para && nomes.get(a.para)) || a.para;
        }
      },
    }));
  }

  /** Busca o saldo atual dos produtos salvos; a API de compras nao retorna estoque. */
  private hidratarEstoquesProdutosSalvos(): void {
    const idsProduto = Array.from(new Set(
      this.products
        .filter(p => p.idProduto && !p.isAvulso)
        .map(p => p.idProduto as number),
    ));
    if (!idsProduto.length) return;

    this.subs.add(
      this.controleEstoqueService.getSaldosProdutos({ idsProduto }).subscribe({
        next: (response) => {
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription || 'Não foi possível carregar o estoque dos produtos.', AlertType.error);
            return;
          }

          const estoquePorProduto = new Map<number, number>();
          for (const saldo of response.list ?? []) {
            estoquePorProduto.set(saldo.idProduto, saldo.saldoDisponivel ?? saldo.saldoFisico ?? 0);
          }

          if (!estoquePorProduto.size) return;

          const estoquePorCompraItem = new Map<number, number>();
          this.products = this.products.map(p => {
            const estoque = p.idProduto ? estoquePorProduto.get(p.idProduto) : undefined;
            if (estoque === undefined) return p;
            if (p.idCompraItem != null) estoquePorCompraItem.set(p.idCompraItem, estoque);
            return { ...p, stock: estoque };
          });

          this.pedidoState = {
            ...this.pedidoState,
            itens: this.pedidoState.itens.map(item => {
              const estoque = item.idCompraItem != null ? estoquePorCompraItem.get(item.idCompraItem) : undefined;
              return estoque === undefined ? item : { ...item, stock: estoque };
            }),
          };
        },
        error: () => this.alertService.show('Erro', 'Não foi possível carregar o estoque dos produtos.', AlertType.error),
      }),
    );
  }

  /**
   * Busca o histórico da compra e reconstrói a timeline. A primeira carga mostra o skeleton e avisa a
   * falha; as recargas (depois de salvar, enviar o pedido ou anotar) trocam a lista no lugar.
   */
  private carregarHistorico(isPrimeiraCarga = false): void {
    if (!this.card.idCompra) return;
    if (isPrimeiraCarga) this.isCarregandoHistorico = true;
    this.subs.add(
      this.compraService.getHistorico(this.card.idCompra).subscribe({
        next: (res) => {
          this.isCarregandoHistorico = false;
          if (res.isError) {
            if (isPrimeiraCarga) this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
            return;
          }
          this.aplicarHistorico(res.listaOcorrencia ?? []);
        },
        error: () => {
          this.isCarregandoHistorico = false;
          if (isPrimeiraCarga) this.alertService.show('Erro', 'Não foi possível carregar o histórico.', AlertType.error);
        },
      }),
    );
  }

  get compraDetalhes() {
    return this.data.detalhe?.compra;
  }

  get solicitanteNome(): string {
    const id = this.compraDetalhes?.idUsuarioSolicitante;
    if (!id) return '';
    return this.data.usersById?.get(id)?.name || String(id);
  }

  // ----- Anexos / Comentários -----
  togglePanel(p: 'anexos' | 'comentarios'): void {
    this.panel = this.panel === p ? null : p;
  }

  /** "Nova anotação" do histórico exige permissão de alterar a compra. */
  get podeAnotar(): boolean {
    return (this.authService.getUserInfos()?.listaPermissoes ?? []).includes(
      PermissaoEnum.Suprimentos_ProcessoCompras_Alterar
    );
  }

  /** Anotação do dia vai sem data, para o servidor gravar a hora do registro; retroativa leva o dia escolhido. */
  registrarAnotacao(anotacao: { descricao: string; dataOcorrencia: Date }): void {
    if (!this.card.idCompra) return;
    const request = new CriarCompraOcorrenciaRequest();
    request.descricao = anotacao.descricao;
    request.dataOcorrencia = anotacao.dataOcorrencia.toDateString() === new Date().toDateString() ? null : anotacao.dataOcorrencia;

    this.isRegistrandoAnotacao = true;
    this.subs.add(
      this.compraService.criarOcorrencia(this.card.idCompra, request).subscribe({
        next: (res) => {
          this.isRegistrandoAnotacao = false;
          if (res.isError) {
            this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
            return;
          }
          this.historicoTimeline?.limparAnotacao();
          this.alertService.show('Sucesso', 'Anotação registrada.', AlertType.success);
          this.carregarHistorico();
        },
        error: () => {
          this.isRegistrandoAnotacao = false;
          this.alertService.show('Erro', 'Não foi possível registrar a anotação.', AlertType.error);
        },
      }),
    );
  }

  /**
   * Split revertido no painel: os itens do pai mudaram e a filha foi arquivada, então o estado
   * editável do card ficou desatualizado. Fecha como 'saved' para o quadro recarregar a coluna.
   */
  onSplitRevertido(): void {
    this.dialogRef.close({ card: this.card, action: 'saved' });
  }

  /** Rejeição registrada no filho da aprovação: atualiza o card e fecha movendo-o. */
  onAprovacaoRegistrada(isAprovado: boolean): void {
    if (this.card.isAprovado === isAprovado) return;
    this.card.isAprovado = isAprovado;
  }

  /** Voto registrado no bloco de aprovação revogada: com a alçada completa, o bloco some. */
  onReaprovacaoRegistrada(isAprovado: boolean): void {
    this.onAprovacaoRegistrada(isAprovado);
    this.reaprovacaoRegistrada = true;
    if (isAprovado) this.aprovacaoRevogadaPendente = false;
  }

  onAprovacaoRejeitada(destino: string): void {
    this.card.stage = destino;
    this.dialogRef.close({ card: this.card, action: 'cancel' });
  }

  get criadoEm(): string {
    const dataInclusao = this.data.detalhe?.compra?.datetimeInclusao;
    return dataInclusao
      ? new Date(dataInclusao).toLocaleDateString('pt-BR')
      : this.card.date;
  }

  // ----- Movimentação de etapa -----
  isFinal(): boolean {
    return this.card.stage === 'finalizado' || this.card.stage === 'cancelado';
  }

  /**
   * Cotação que já gerou os pedidos: fica só como registro de origem. Abre em leitura —
   * sem salvar e sem mover, senão avançá-la de novo geraria os mesmos pedidos em duplicata.
   */
  get isArquivada(): boolean {
    return !!this.data.detalhe?.compra?.isArquivado;
  }

  /** Card sem edição: etapa terminal (finalizado/cancelado) ou cotação já arquivada. */
  get isSomenteLeitura(): boolean {
    return this.isFinal() || this.isArquivada;
  }

  /**
   * Salvar indisponível: Cancelado e cotação arquivada. Finalizado salva só a observação de encerramento (D-16) — os
   * demais campos ficam somente leitura (`isSomenteLeitura`) e o backend os ignora.
   */
  get isSalvamentoBloqueado(): boolean {
    return this.card.stage === 'cancelado' || this.isArquivada;
  }

  /** Motivo do "Salvar e fechar" desabilitado (vazio = habilitado ou salvando); alimenta o tooltip do botão. */
  get motivoSalvarBloqueado(): string {
    if (this.saving) return '';
    if (this.card.stage === 'cancelado') return 'Compra cancelada: o card não pode mais ser alterado.';
    if (this.isArquivada) return 'Cotação arquivada: o card fica somente para consulta.';
    return this.isDirty ? '' : 'Nenhuma alteração para salvar.';
  }

  /** Etapas em que o card marca "Pedido sem nota fiscal" (as mesmas de onde ele vai direto para Finalizado). */
  get isEtapaSemNotaFiscal(): boolean {
    return ETAPAS_FINALIZACAO_SEM_NF.has(this.card.stage);
  }

  /**
   * Pedido marcado sem NF-e (estado da tela) numa etapa de onde vai direto para Finalizado. Marcado e não salvo, o
   * "Salvar e mover" do moveTo grava a marca antes do movimento.
   */
  get isFinalizacaoSemNf(): boolean {
    return this.isSemNotaFiscal && this.isEtapaSemNotaFiscal;
  }

  get forwardMoves(): MoveForward[] {
    const allowed = this.transitions[this.card.stage] || [];
    const mainKeys = this.mainFlowKeys;
    const currentMainIdx = mainKeys.indexOf(this.card.stage);
    // Pedido sem NF-e: Faturado, Conferência e Divergência saem do menu e Finalizado entra, mesmo fora das movimentações configuradas.
    const isFinalizacaoSemNf = this.isFinalizacaoSemNf;
    const destinos = isFinalizacaoSemNf
      ? [...allowed.filter(k => k !== 'faturado' && k !== 'conferencia' && k !== 'divergencia' && k !== 'finalizado'), 'finalizado']
      : allowed;
    return destinos
      .filter(k => k !== 'cancelado')
      .filter(k => this.allStages.some(s => s.key === k))
      .filter(k => currentMainIdx === -1 || mainKeys.indexOf(k) === -1 || mainKeys.indexOf(k) > currentMainIdx)
      .map(k => {
        if (isFinalizacaoSemNf && k === 'finalizado') {
          return { to: k, label: 'Finalizar sem nota fiscal', kind: 'skip' } as MoveForward;
        }
        const destMainIdx = mainKeys.indexOf(k);
        const isDanger = k === 'divergencia';
        const isSkip = !isDanger && currentMainIdx >= 0 && destMainIdx >= 0 && destMainIdx > currentMainIdx + 1;
        return { to: k, label: this.allStages.find(s => s.key === k)!.name, kind: isDanger ? 'danger' : isSkip ? 'skip' : 'next' } as MoveForward;
      });
  }

  get backMoves(): string[] {
    const allowed = this.transitions[this.card.stage] || [];
    const mainKeys = this.mainFlowKeys;
    const currentMainIdx = mainKeys.indexOf(this.card.stage);
    if (currentMainIdx === -1) return [];
    return allowed.filter(k => {
      if (k === 'cancelado') return false;
      const destMainIdx = mainKeys.indexOf(k);
      return destMainIdx >= 0 && destMainIdx < currentMainIdx;
    });
  }

  get isTerminalMove(): boolean {
    return (this.transitions[this.card.stage] || []).length === 0;
  }

  /** "Cancelar compra" só com a movimentação para Cancelado configurada na etapa atual (mesma regra do arraste no quadro). */
  readonly semPermissaoCancelarCompra = 'Seu perfil não tem permissão para cancelar compras.';

  get podeCancelar(): boolean {
    return (this.transitions[this.card.stage] || []).includes('cancelado');
  }

  /**
   * Motivo do retorno bloqueado (tooltip); vazio = liberado. Mesma regra do PUT (CA-03): pedido enviado ao fornecedor
   * não volta para etapa anterior ao Pedido de Compra (backMoves já traz só etapas do fluxo principal).
   */
  motivoRetornoBloqueado(destinoKey: string): string {
    if (!this.pedidoState.envio.datetimeEnvio) return '';
    const destino = this.allStages.findIndex(s => s.key === destinoKey);
    const pedido = this.allStages.findIndex(s => s.key === 'pedido');
    return pedido >= 0 && destino >= 0 && destino < pedido
      ? 'Pedido já enviado ao fornecedor: não pode voltar para uma etapa anterior ao Pedido de Compra.'
      : '';
  }

  moveKindLabel(kind: string): string {
    switch (kind) {
      case 'skip': return 'Pular para';
      case 'danger': return 'Reprovar';
      case 'alt': return 'Avançar com ressalva';
      default: return 'Avançar para';
    }
  }

  stageByKey(key: string): KanbanStage | undefined {
    return this.allStages.find(s => s.key === key);
  }

  moveTo(key: string): void {
    // Mesma trava do backend (avanço exige os obrigatórios da etapa atual), checada com o que está na
    // tela: recusa aqui mantém o card aberto, em vez de fechar e só então o quadro mostrar o erro.
    const pendentes = this.camposObrigatoriosPendentesParaAvanco(key);
    if (pendentes.length > 0) {
      this.alertService.show('Atenção', `Preencha os campos obrigatórios antes de mover o card: ${pendentes.join(', ')}.`, AlertType.warning);
      setTimeout(() => {
        const campo = this.elementRef.nativeElement.querySelector<HTMLElement>('.ced-form-field--invalid, [required].ng-invalid');
        campo?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const foco = campo?.matches('input, textarea, mat-select') ? campo : campo?.querySelector<HTMLElement>('input, textarea, mat-select');
        foco?.focus({ preventScroll: true });
      });
      return;
    }

    // Edições não salvas: avisa antes de mover (o movimento reabre/fecha o card e perderia o que
    // foi digitado). Deixa salvar-e-mover ou cancelar o movimento.
    if (!this.isDirty) {
      this.moverComSelecao(key);
      return;
    }

    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Alterações não salvas',
        description: 'Há alterações não salvas neste card. Deseja salvar antes de mover?',
        buttonConfirm: 'Salvar e mover',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe((result) => {
      if (!result?.confirm) return;
      // Salva as edições da compra e só então movimenta (mantém o valor/itens consistentes downstream).
      // Sem sugerir o envio do formulário de cotação: o card está saindo da etapa, e convidar
      // fornecedor para cotar já não faz sentido no destino.
      // O save não devolve o total recalculado: o valor gravado vai no card do fechamento (mesmo
      // tratamento de onNotaAtualizada), senão o quadro mostra o valor anterior no destino.
      this.doSave(false, () => {
        this.subs.add(this.compraService.getDetalhe(this.card.idCompra!).subscribe({
          next: detalhe => {
            if (!detalhe.isError && detalhe.compra) this.card.value = fmtMoneyDisplay(detalhe.compra.valorTotal);
            this.moverComSelecao(key);
          },
          error: () => this.moverComSelecao(key),
        }));
      }, undefined, false);
    });
  }

  /**
   * Ao sair da Cotação avançando (Aprovação ou Pedido), aplica a seleção montada à compra
   * (preços dos itens + valor total) antes de fechar. Sem isso, o valorTotal fica 0 e a Aprovação
   * avalia a alçada / mostra "Valor avaliado" errado.
   */
  /**
   * A compra só tem fornecedor definido a partir do Pedido: antes disso a Cotação ainda
   * está escolhendo, e uma cotação com vários vencedores gera um pedido por fornecedor no
   * avanço — cada pedido nasce já com o seu. Índice em allStages porque as etapas do fluxo
   * principal vêm ordenadas por `ordem`.
   */
  get mostrarFornecedor(): boolean {
    if (!this.idFornecedor) return false;

    const atual = this.allStages.findIndex(s => s.key === this.card.stage);
    const pedido = this.allStages.findIndex(s => s.key === 'pedido');
    return atual >= 0 && pedido >= 0 && atual >= pedido;
  }

  /** Na Conferência com conferência cega não concluída, as quantidades ficam ocultas para todos. */
  get ocultarQuantidades(): boolean {
    return this.card.stage === 'conferencia' && this.conferenciaCegaPendente;
  }

  /**
   * Lista compacta de produtos na coluna da esquerda: só nas etapas depois de Pedido de Compra
   * (inclusive Cancelado e Divergência, que vêm depois em allStages). Até o Pedido os produtos
   * são editados no conteúdo da etapa, e repeti-los aqui seria duplicar a lista.
   */
  get mostrarProdutosResumo(): boolean {
    const atual = this.allStages.findIndex(s => s.key === this.card.stage);
    const pedido = this.allStages.findIndex(s => s.key === 'pedido');
    return atual >= 0 && pedido >= 0 && atual > pedido;
  }

  onPedidoFornecedorChange(e: { idFornecedor: number; nome: string }): void {
    this.idFornecedor = e.idFornecedor;
    this.nomeFornecedor = e.nome;
  }

  /** A etapa Pedido já tirou o item de pedidoState.itens; aqui sai o de mesmo índice em products. */
  onPedidoProdutoRemovido(index: number): void {
    this.products = this.products.filter((_, i) => i !== index);
  }

  private carregarNomeFornecedor(): void {
    if (!this.idFornecedor) return;

    this.pessoaLookupService.getListPessoaIds({ listIdPessoa: [this.idFornecedor] }).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar o fornecedor.', AlertType.error);
          return;
        }
        const p = (res.listaPessoa || [])[0];
        if (p) this.nomeFornecedor = p.nome || p.nomeFantasia || '';
      },
      error: () => this.alertService.show('Erro', 'Não foi possível carregar o fornecedor.', AlertType.error),
    });
  }

  /**
   * Espelha GetItensSemVencedor do backend: quantos itens ainda não têm fornecedor vencedor
   * com preço na seleção atual. O backend é quem decide, mas checar aqui evita fechar o
   * diálogo para só então mostrar o erro vindo da movimentação.
   */
  private itensSemVencedor(): number {
    const selecionados = new Set(
      (this.cotacaoStage?.cotSelection ?? [])
        .filter(s => s.idCompraItem != null && s.idFornecedorSel != null && (s.valorUnitarioSel ?? 0) > 0)
        .map(s => s.idCompraItem)
    );

    return this.products.filter(p => !p.idCompraItem || !selecionados.has(p.idCompraItem)).length;
  }

  private moverComSelecao(destinoKey: string): void {
    // Item avulso (Pedido) e Faturado sem NF não são antecipados aqui: o quadro move pelo PUT, que
    // valida as demais regras (campos, vencedores, aprovação, permissão...) antes e só
    // então devolve isExigeAssociacaoProduto/isExigeNotaFiscal para abrir o modal — a mesma fila do arraste.
    // Fecha com uma cópia na etapa de destino: mudar this.card.stage montaria o painel do destino no diálogo que está
    // fechando (no Finalizado, a consulta da situação dos lançamentos começava e era abortada).
    const fechar = () => this.dialogRef.close({ card: { ...this.card, stage: destinoKey }, action: 'advance' });
    // O filho da cotação está garantidamente vivo quando a etapa atual é 'cotacao' (@switch).
    if (this.card.stage === 'cotacao' && (destinoKey === 'pedido' || destinoKey === 'aprovacao') && this.cotacaoStage) {
      // Mesma regra do backend: seleção vazia avança (o pedido é montado na etapa Pedido) e completa
      // avança; só a parcial é recusada.
      const pendentes = this.itensSemVencedor();
      if (pendentes > 0 && pendentes < this.products.length) {
        this.alertService.show(
          'Atenção',
          `Seleção incompleta: defina o fornecedor vencedor e o preço de todos os itens antes de avançar da Cotação (${pendentes} pendente(s)).`,
          AlertType.warning
        );
        return;
      }
      this.cotacaoStage.persistirSelecaoCotacao(fechar);
    } else {
      fechar();
    }
  }

  cancel(): void {
    if (!this.isDirty) {
      // Título salvo à parte (endpoint próprio) não passa por isDirty/doSave — sem isso o
      // fechamento devolveria null e o quadro ficaria com o título antigo.
      if (this.tituloAlterado || this.conferenciaAlterada || this.lancamentoAlterado || this.reaprovacaoRegistrada) {
        this.dialogRef.close({ card: this.card, action: 'saved' });
      }
      else this.dialogRef.close(null);
      return;
    }

    // X chama dialogRef.close() direto no componente padrão (undefined) — mesmo
    // resultado de clicar fora/Esc: fecha só o aviso, card continua aberto.
    // "Descartar e fechar" é o único caminho que fecha o card perdendo as edições.
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Alterações não salvas',
        description: 'Você tem alterações que ainda não foram salvas. O que deseja fazer?',
        buttonClose: 'Descartar e fechar',
        buttonConfirm: 'Salvar e fechar',
      },
    }).afterClosed().subscribe((result) => {
      if (result?.confirm) {
        this.saveAndClose();
      } else if (result) {
        // Descartar não desfaz o que a etapa já gravou (aprovação, cotação): o quadro recarrega a coluna ao fechar.
        this.dialogRef.close(null);
      }
      // result === undefined (X/backdrop/Esc): nada acontece, card continua aberto.
    });
  }

  onNotaAtualizada(): void {
    if (!this.card.idCompra) return;
    this.subs.add(this.compraService.getDetalhe(this.card.idCompra).subscribe({
      next: detalhe => {
        if (!detalhe.isError && detalhe.compra) this.card.value = fmtMoneyDisplay(detalhe.compra.valorTotal);
        this.dialogRef.close({ card: this.card, action: 'saved' });
      },
      error: () => this.dialogRef.close({ card: this.card, action: 'saved' }),
    }));
  }

  save(): void { this.doSave(false); }
  saveAndClose(): void { this.doSave(true); }

  private doSave(closeAfter: boolean, onSuccess?: () => void, onError?: () => void, sugerirEnvioCotacao = true): void {
    if (!this.card.idCompra || this.saving || this.isSalvamentoBloqueado) {
      onError?.();
      return;
    }
    // Finalizado só grava a observação de encerramento (D-16): o resto é somente leitura, então não revalida.
    const isFinalizado = this.card.stage === 'finalizado';
    if (!isFinalizado && !this.camposObrigatoriosPreenchidos()) {
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios da etapa.', AlertType.warning);
      onError?.();
      return;
    }
    // Quantidade é validada aqui, e não no input: o campo aceita decimal e o clamp em tempo de
    // digitação divergia da tela (ver updateQuantity em ced-stage-solicitacao).
    const itensParaValidar = this.card.stage === 'pedido' ? this.pedidoState.itens : this.products;
    const semQuantidade = itensParaValidar.filter(i => !(i.qty > 0)).map(i => i.name);
    if (!isFinalizado && semQuantidade.length > 0) {
      this.alertService.show('Atenção', `Informe uma quantidade maior que zero para: ${semQuantidade.join(', ')}.`, AlertType.warning);
      onError?.();
      return;
    }
    // Só onde a marca é editável: nas demais etapas ela segue como está gravada.
    if (this.isFinalizacaoSemNf && !this.motivoSemNotaFiscal.trim()) {
      this.alertService.show('Atenção', 'Informe o motivo da compra sem nota fiscal.', AlertType.warning);
      onError?.();
      return;
    }
    this.saving = true;
    const prioMap: Record<string, number> = {
      baixa: TipoPrioridadeEnum.Baixa,
      media: TipoPrioridadeEnum.Media,
      alta: TipoPrioridadeEnum.Alta,
    };
    // Tributos/preço por item: base nos valores persistidos; sobrescreve com a edição do pedido.
    // Garante que salvar de qualquer etapa preserve os tributos já gravados (o repo recria os itens).
    const itemFin = new Map<number, { quantidade: number | null; valorUnitario: number | null; valorDesconto: number | null; valorIpi: number | null; valorIcmsSt: number | null; valorIbs: number | null; valorCbs: number | null }>();
    for (const i of this.data.detalhe?.itens ?? []) {
      if (i.idCompraItem != null) {
        itemFin.set(i.idCompraItem, {
          quantidade: i.quantidade ?? null,
          valorUnitario: i.valorUnitario ?? null,
          valorDesconto: i.valorDesconto ?? null,
          valorIpi: i.valorIpi ?? null,
          valorIcmsSt: i.valorIcmsSt ?? null,
          valorIbs: i.valorIbs ?? null,
          valorCbs: i.valorCbs ?? null,
        });
      }
    }
    // Itens cujo preço veio da edição da etapa Pedido: para eles `itemFin` é a fonte de verdade,
    // não `products` (que guarda o preço da cotação e sobrescreveria a edição — ver abaixo).
    const idsEditadosNoPedido = new Set<number>();
    if (this.card.stage === 'pedido') {
      for (const it of this.pedidoState.itens) {
        if (it.idCompraItem != null) {
          idsEditadosNoPedido.add(it.idCompraItem);
          itemFin.set(it.idCompraItem, {
            quantidade: it.qty,
            valorUnitario: it.preco ?? 0,
            valorDesconto: it.desconto ?? 0,
            valorIpi: it.ipi ?? 0,
            valorIcmsSt: it.st ?? 0,
            valorIbs: it.ibs ?? 0,
            valorCbs: it.cbs ?? 0,
          });
        }
      }
    }

    const request: AtualizarCompraRequest = {
      idTipoPrioridade: prioMap[this.card.prio] ?? TipoPrioridadeEnum.Media,
      idUsuarioResponsavel: this.card.idUsuarioResponsavel ?? null,
      setor: this.setor.trim() || null,
      dataEntregaDesejada: this.dataEntrega ? toApiDate(this.dataEntrega) : null,
      justificativa: this.justificativa.trim() || null,
      idCriterioCotacao: this.cotCriterioIndex,
      justificativaCotacao: this.cotJustificativa.trim() || null,
      itens: this.products.map((p, idx) => {
        // Item incluído na etapa Pedido ainda não tem idCompraItem: os valores digitados lá vêm do item
        // de mesmo índice em pedidoState.itens (a etapa mantém as duas listas alinhadas).
        const novoNoPedido = this.card.stage === 'pedido' && p.idCompraItem == null ? this.pedidoState.itens[idx] : undefined;
        const fin = p.idCompraItem != null
          ? itemFin.get(p.idCompraItem)
          : novoNoPedido && {
            quantidade: novoNoPedido.qty,
            valorUnitario: novoNoPedido.preco ?? 0,
            valorDesconto: novoNoPedido.desconto ?? 0,
            valorIpi: novoNoPedido.ipi ?? 0,
            valorIcmsSt: novoNoPedido.st ?? 0,
            valorIbs: novoNoPedido.ibs ?? 0,
            valorCbs: novoNoPedido.cbs ?? 0,
          };
        const usaValoresDoPedido = novoNoPedido != null || (p.idCompraItem != null && idsEditadosNoPedido.has(p.idCompraItem));
        return {
          idCompraItem: p.idCompraItem ?? null,
          idProduto: p.idProduto ?? null,
          codigoSku: p.sku || null,
          nome: p.name,
          marca: p.marca ?? null,
          isAvulso: p.isAvulso ?? false,
          // Mesma precedência do preço: na etapa Pedido a quantidade digitada lá é a mais recente.
          quantidade: usaValoresDoPedido ? (fin?.quantidade ?? p.qty) : p.qty,
          unidade: p.unit,
          // Preço: `products` tem precedência porque reflete a seleção da cotação recém aplicada —
          // sem isso, "Salvar e fechar" depois de aplicar a matriz reverteria para o preço antigo
          // do snapshot. A exceção é o item editado na etapa Pedido, onde o campo digitado é a
          // decisão mais recente e `products` guarda o preço da cotação.
          valorUnitario: usaValoresDoPedido
            ? (fin?.valorUnitario ?? null)
            : (p.valorUnitario ?? fin?.valorUnitario ?? null),
          valorDesconto: fin?.valorDesconto ?? null,
          valorIpi: fin?.valorIpi ?? null,
          valorIcmsSt: fin?.valorIcmsSt ?? null,
          valorIbs: fin?.valorIbs ?? null,
          valorCbs: fin?.valorCbs ?? null,
          observacao: p.observacao ?? null,
        };
      }),
      // Fornecedor só é escolhido na etapa Pedido; o backend ignora nas demais.
      idFornecedor: this.card.stage === 'pedido' ? this.pedidoState.envio.idFornecedor : null,
      campos: this.camposEtapa.map(c => ({
        idCampoDefinicao: c.idCampoDefinicao,
        valor: this.valorCampoParaSalvar(c),
        idCampoDefinicaoOpcao: c.idTipoCampo === 5 ? (c.idCampoDefinicaoOpcao ?? null) : null,
      })),
      // Pedido de compra — sempre reenviado para preservar entre etapas.
      condicaoPagamento: this.pedidoState.formaPagamento || null,
      prazoEntrega: this.pedidoState.prazoEntrega.trim() || null,
      valorFrete: parseBR(this.pedidoState.frete),
      valorOutrasDespesas: parseBR(this.pedidoState.outrasDespesas),
      idCentroCusto: this.pedidoState.centroCusto ?? null,
      idCategoriaFinanceira: this.pedidoState.categoriaDre ?? null,
      observacoesPedido: this.pedidoState.observacoes.trim() || null,
      parcelas: this.pedidoState.parcelas.map(p => ({
        sequencia: p.sequencia,
        dias: p.dias ?? null,
        dataVencimento: null,
        valor: parseBR(p.valor),
        idFormaPagamento: null,
      })),
      // Finalizado.
      observacaoEncerramento: this.finalizadoObservacao.trim() || null,
      // Sem NF-e: estado da tela (editável em Pedido, Aprovação e Faturado); desmarcado não leva motivo.
      isSemNotaFiscal: this.isSemNotaFiscal,
      motivoSemNotaFiscal: this.isSemNotaFiscal ? (this.motivoSemNotaFiscal.trim() || null) : null,
    };
    // Listas como foram enviadas: a tela pode ganhar ou perder itens enquanto o PUT está em andamento.
    const produtosEnviados = this.products;
    const itensPedidoEnviados = this.pedidoState.itens;
    this.compraService.atualizar(this.card.idCompra, request).subscribe({
      next: (res) => {
        if (res.isError) {
          this.saving = false;
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível salvar.', AlertType.error);
          onError?.();
          return;
        }
        // O quadro usa a marca do card (arraste direto para Finalizado e selo "Sem NF").
        this.card.isSemNotaFiscal = request.isSemNotaFiscal ?? false;
        // Produto incluído neste save ainda está sem idCompraItem na tela. Sem o id, um novo save o
        // reinseriria e o backend apagaria o item gravado junto com as linhas de cotação dele. O PUT
        // descarta itens sem nome ou sem quantidade e devolve os gravados na ordem do request: aplica o
        // mesmo filtro para casar cada gravado com o item enviado (pedidoState.itens segue o índice de
        // `products`) e grava o id pela referência do objeto, não pelo índice atual da tela. O rebind
        // de [products] leva os ids à etapa de cotação (ver ngOnChanges lá).
        const itensGravados = res.itens ?? [];
        const idPorProduto = new Map<CardProduct, number>();
        const idPorItemPedido = new Map<PedidoItemView, number>();
        const pedidoAlinhado = itensPedidoEnviados.length === produtosEnviados.length;
        let gravado = 0;
        request.itens.forEach((item, idx) => {
          if (!item.nome?.trim() || !(item.quantidade > 0)) return;
          const idCompraItem = itensGravados[gravado++]?.idCompraItem;
          if (idCompraItem == null) return;
          if (produtosEnviados[idx].idCompraItem == null) idPorProduto.set(produtosEnviados[idx], idCompraItem);
          if (pedidoAlinhado && itensPedidoEnviados[idx].idCompraItem == null) {
            idPorItemPedido.set(itensPedidoEnviados[idx], idCompraItem);
          }
        });
        if (idPorProduto.size > 0) {
          this.products = this.products.map(p => idPorProduto.has(p) ? { ...p, idCompraItem: idPorProduto.get(p) } : p);
        }
        if (idPorItemPedido.size > 0) {
          this.pedidoState = {
            ...this.pedidoState,
            itens: this.pedidoState.itens.map(it => idPorItemPedido.has(it) ? { ...it, idCompraItem: idPorItemPedido.get(it) } : it),
          };
        }
        // Convites, condições de pagamento e o envio automático da cotação vivem em outro
        // endpoint (CotacaoFornecedor) e só acontecem aqui — depois da compra gravada, porque o
        // e-mail é montado no backend a partir do que está persistido.
        this.persistirCotacoesDaEtapa(
          sugerirEnvioCotacao,
          () => {
            this.saving = false;
            this.isDirty = false;
            if (onSuccess) {
              onSuccess();
            } else if (closeAfter) {
              this.dialogRef.close({ card: this.card, action: 'saved' });
            } else {
              this.alertService.show('Sucesso', 'Alterações salvas com sucesso.', AlertType.success);
              // Recarrega o histórico para refletir a ocorrência de edição (com o diff) na timeline.
              this.carregarHistorico();
            }
          },
          () => {
            this.saving = false;
            onError?.();
          },
        );
      },
      error: () => {
        this.saving = false;
        this.alertService.show('Erro', 'Não foi possível salvar.', AlertType.error);
        onError?.();
      },
    });
  }

  /** Etapa de cotação tem estado próprio para gravar; nas demais o save da compra basta. */
  private persistirCotacoesDaEtapa(sugerirEnvio: boolean, onDone: () => void, onError: () => void): void {
    if (this.card.stage === 'cotacao' && this.cotacaoStage) {
      this.cotacaoStage.persistirCotacoes(onDone, onError, sugerirEnvio);
    } else {
      onDone();
    }
  }

  /**
   * Envio do pedido ao fornecedor (emitido pelo filho da etapa de pedido com o
   * destinatário já coletado). Salva a compra ANTES — o documento/e-mail é gerado
   * no backend a partir do que está persistido — e então dispara o envio,
   * atualizando o espelho compartilhado (state.envio) que alimenta o badge.
   */
  onEnviarPedido(payload: EnviarPedidoPayload): void {
    if (!this.card.idCompra || this.pedidoState.envio.enviando) return;

    this.pedidoState.envio.enviando = true;
    this.doSave(false,
      () => {
        // Expande os perfis notificados antes de enviar: o backend não resolve perfil -> usuários.
        this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.EnvioPedidoFornecedor).pipe(
          switchMap(idsUsuarioPerfil => this.compraPedidoService.enviarPedidoFornecedor(
            this.card.idCompra!, payload.destinatario, payload.nomeFornecedor, payload.documento, idsUsuarioPerfil,
          )),
        ).subscribe({
          next: (res) => {
            this.pedidoState.envio.enviando = false;
            if (res.isError) {
              this.alertService.show('Erro', res.errorDescription || 'Não foi possível enviar o pedido.', AlertType.error);
              return;
            }
            this.pedidoState.envio.datetimeEnvio = res.datetimeEnvioPedido ?? new Date().toISOString();
            this.pedidoState.envio.datetimeConfirmacao = res.datetimeConfirmacaoPedido ?? this.pedidoState.envio.datetimeConfirmacao;
            this.alertService.show('Sucesso', `Pedido enviado para ${payload.destinatario}.`, AlertType.success);
            this.carregarHistorico();
          },
          error: () => {
            this.pedidoState.envio.enviando = false;
            this.alertService.show('Erro', 'Não foi possível enviar o pedido.', AlertType.error);
          },
        });
      },
      () => { this.pedidoState.envio.enviando = false; },
    );
  }

  onCampoEtapaChange(): void {
    this.isDirty = true;
  }

  isCampoObrigatorioInvalido(campo: CardDynamicField): boolean {
    if (!campo.isObrigatorio) return false;
    return !this.campoTemValor(campo);
  }

  vinculoApiUrl(campo: CardDynamicField): string {
    const entidade = (campo.opcoes?.[0]?.descricao || 'pessoa').toLowerCase();
    return entidade === 'produto'
      ? `${environment.urlApiProduto}Lookup/listProdutoSelect`
      : `${environment.urlApiPessoa}Lookup/listPessoaSelect`;
  }

  vinculoControl(campo: CardDynamicField): FormControl<number | null> {
    let control = this.vinculoControls.get(campo.idCampoDefinicao);
    if (!control) {
      const salvo = this.parseVinculoValue(campo.valor);
      control = new FormControl<number | null>({ value: salvo?.id ?? null, disabled: this.isSomenteLeitura });
      this.vinculoControls.set(campo.idCampoDefinicao, control);
      this.vinculoPreloads.set(campo.idCampoDefinicao, salvo ? [salvo] : []);
    }
    return control;
  }

  // A referência precisa ser estável: um array novo por ciclo faz o ngOnChanges do
  // select-pesquisavel reemitir e realimentar o change detection (trava a tela).
  vinculoPreload(campo: CardDynamicField): VinculoOpcao[] {
    let preload = this.vinculoPreloads.get(campo.idCampoDefinicao);
    if (!preload) {
      preload = [];
      this.vinculoPreloads.set(campo.idCampoDefinicao, preload);
    }
    return preload;
  }

  onVinculoChange(campo: CardDynamicField, selecionado: VinculoOpcao | number | null): void {
    if (selecionado == null) {
      campo.valor = null;
      this.onCampoEtapaChange();
      return;
    }
    const opcao: VinculoOpcao = typeof selecionado === 'number'
      ? { id: selecionado, descricao: this.vinculoPreload(campo).find(o => o.id === selecionado)?.descricao ?? '' }
      : { id: selecionado.id, descricao: selecionado.descricao ?? '' };
    this.vinculoPreloads.set(campo.idCampoDefinicao, [opcao]);
    campo.valor = JSON.stringify(opcao);
    this.onCampoEtapaChange();
  }

  private parseVinculoValue(valor?: string | null): VinculoOpcao | null {
    if (!valor) return null;
    try {
      const parsed = JSON.parse(valor);
      return typeof parsed?.id === 'number' ? { id: parsed.id, descricao: parsed.descricao ?? '' } : null;
    } catch {
      return null;
    }
  }

  private camposObrigatoriosPreenchidos(): boolean {
    // Justificativa é um campo nativo: só exige quando configurado obrigatório na etapa.
    if (this.card.stage === 'solicitacao' && this.justificativaObrigatoria && !this.justificativa.trim()) {
      return false;
    }
    return this.camposEtapa.every(c => !c.isObrigatorio || this.campoTemValor(c));
  }

  /**
   * Espelha GetCamposObrigatoriosPendentes do backend com o estado da tela: só o avanço (destino não
   * especial e depois da etapa atual em allStages, ordenado por `ordem`) exige os obrigatórios da etapa.
   * Nativos sem estado editável no card (prioridade, solicitante, valor total) ficam com o backend.
   */
  private camposObrigatoriosPendentesParaAvanco(destinoKey: string): string[] {
    const destino = this.stageByKey(destinoKey);
    const isAvanco = !!destino && !destino.isEspecial
      && this.allStages.indexOf(destino) > this.allStages.findIndex(s => s.key === this.card.stage);
    if (!isAvanco) return [];

    const pendentes = this.camposEtapa.filter(c => c.isObrigatorio && !this.campoTemValor(c)).map(c => c.nome);
    const idFornecedor = this.card.stage === 'pedido' ? this.pedidoState.envio.idFornecedor : this.idFornecedor;
    const nativos: [CampoNativoEnum, string, boolean][] = [
      [CampoNativoEnum.Setor, 'Setor', !!this.setor.trim()],
      [CampoNativoEnum.EntregaDesejada, 'Entrega desejada', !!this.dataEntrega],
      [CampoNativoEnum.Justificativa, 'Justificativa', !!this.justificativa.trim()],
      [CampoNativoEnum.Fornecedor, 'Fornecedor', !!idFornecedor],
      [CampoNativoEnum.CondicaoPagamento, 'Condição de pagamento', !!this.pedidoState.formaPagamento.trim()],
    ];
    for (const [id, nome, preenchido] of nativos)
      if (this.nativosObrigatorios.has(id) && !preenchido) pendentes.push(nome);
    return pendentes;
  }

  private campoTemValor(campo: CardDynamicField): boolean {
    if (campo.idTipoCampo === 2 || campo.idTipoCampo === 3) return campo.valorNumero != null;
    if (campo.idTipoCampo === 4) return !!campo.valorData && !isNaN(campo.valorData.getTime());
    if (campo.idTipoCampo === 5) return campo.idCampoDefinicaoOpcao != null;
    if (campo.idTipoCampo === 6) return (campo.valorMultiselect || []).length > 0 || !!campo.valor?.trim();
    return !!campo.valor?.trim();
  }

  private valorCampoParaSalvar(campo: CardDynamicField): string | null {
    if (campo.idTipoCampo === 2 || campo.idTipoCampo === 3) {
      return campo.valorNumero != null ? String(campo.valorNumero) : null;
    }
    if (campo.idTipoCampo === 4) {
      return campo.valorData && !isNaN(campo.valorData.getTime()) ? campo.valorData.toISOString() : null;
    }
    if (campo.idTipoCampo === 5) {
      const opcao = campo.opcoes.find(o => o.idCampoDefinicaoOpcao === campo.idCampoDefinicaoOpcao);
      return opcao?.descricao ?? null;
    }
    if (campo.idTipoCampo === 6 && campo.opcoes.length) {
      return (campo.valorMultiselect || []).length ? JSON.stringify(campo.valorMultiselect) : null;
    }
    return campo.valor?.trim() || null;
  }

  private parseMultiselectValue(valor?: string | null): number[] {
    if (!valor) return [];
    try {
      const parsed = JSON.parse(valor);
      return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
    } catch {
      return valor.split(',').map(v => Number(v.trim())).filter(Number.isFinite);
    }
  }

  private parseNumeroValue(valor?: string | null): number | null {
    if (valor == null || valor === '') return null;
    const parsed = Number(valor);
    return Number.isFinite(parsed) ? parsed : null;
  }

  onCampoDateInput(event: Event): void {
    MaskService.onDateInput(event);
    this.onCampoEtapaChange();
  }

  /** Estoque ou financeiro lançado ou excluído no painel do Finalizado: o histórico recarrega e o quadro recarrega ao fechar. */
  onLancamentoAlterado(): void {
    this.lancamentoAlterado = true;
    this.carregarHistorico();
  }

  /**
   * D-13: conferência da NF concluída com a automação "Mover automaticamente" (gatilho Concluiu conferência, destino
   * Finalizado) ativa — fecha e o quadro finaliza todos os pedidos da NF pelo fluxo de finalização. Com edição não
   * salva, salva antes; se o salvamento falhar, o card fica onde está.
   */
  onConferenciaConcluida(idNFeEntrada: number): void {
    const finalizado = this.stageByKey('finalizado');
    if (!finalizado?.idEtapaKanban) return;
    this.subs.add(this.automacaoService.automacaoAtiva(TipoAutomacaoEnum.MoverAutomaticamente).subscribe(automacao => {
      if (automacao?.idTipoOcorrenciaCompra !== TipoOcorrenciaCompraEnum.ConclusaoConferencia
        || automacao.idEtapaKanbanDestino !== finalizado.idEtapaKanban) return;
      const fechar = () => this.dialogRef.close({ card: this.card, action: 'finalizar-automatico', idNFeEntrada });
      if (this.isDirty) this.doSave(false, fechar, undefined, false);
      else fechar();
    }));
  }

  /** Desfecho escolhido no painel da etapa Divergência: segue pelos fluxos existentes de mover e cancelar. */
  onDesfechoDivergencia(desfecho: DesfechoDivergenciaEnum): void {
    switch (desfecho) {
      case DesfechoDivergenciaEnum.Finalizar: this.moveTo('finalizado'); break;
      case DesfechoDivergenciaEnum.VoltarConferencia: this.moveTo('conferencia'); break;
      case DesfechoDivergenciaEnum.Cancelar: this.cancelCard(); break;
      case DesfechoDivergenciaEnum.AceitarSeguir: this.aceitarDivergenciaVinculo(); break;
      case DesfechoDivergenciaEnum.ReverVinculo: {
        // Mesma NF do modo NF; sem ela, a de vínculo mais recente do pedido.
        const vinculos = this.data.detalhe?.vinculosNota ?? [];
        const idNFeEntrada = this.notaModoNf?.idNFeEntrada ?? vinculos[vinculos.length - 1]?.idNFeEntrada;
        if (!idNFeEntrada || !this.notaStage) {
          this.alertService.show('Atenção', 'Nenhuma NF vinculada ao pedido para rever o vínculo.', AlertType.warning);
          break;
        }
        void this.notaStage.abrirVinculo(idNFeEntrada);
        break;
      }
    }
  }

  /** Aceita as divergências do vínculo (preço/saldo do pedido passam a ser os da NF-e) e segue para Pedido Faturado. */
  private aceitarDivergenciaVinculo(): void {
    if (!this.card.idCompra) return;
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Aceitar divergências do vínculo',
        description: 'O preço e o saldo do pedido serão ajustados ao da NF-e e o pedido segue para Pedido Faturado.',
        buttonConfirm: 'Aceitar e seguir',
        buttonClose: 'Voltar',
      },
    }).afterClosed().subscribe((result) => {
      if (!result?.confirm) return;
      this.subs.add(this.compraService.aceitarDivergenciaVinculo(this.card.idCompra!).subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível aceitar as divergências do vínculo.', AlertType.error);
            return;
          }
          // Valor do pedido mudou com o preço da NF: o card do fechamento leva o total atualizado.
          this.subs.add(this.compraService.getDetalhe(this.card.idCompra!).subscribe({
            next: detalhe => {
              if (!detalhe.isError && detalhe.compra) this.card.value = fmtMoneyDisplay(detalhe.compra.valorTotal);
              this.moveTo('faturado');
            },
            error: () => this.moveTo('faturado'),
          }));
        },
        error: () => this.alertService.show('Erro', 'Não foi possível aceitar as divergências do vínculo.', AlertType.error),
      }));
    });
  }

  cancelCard(): void {
    // Também chamado pelo desfecho da Divergência: trava o duplo envio fora do botão do painel.
    if (this.cancelando) return;
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Cancelar compra',
        innerHtml: 'Esta ação move a compra para <b>Cancelado</b> e registra o motivo no histórico. Informe a justificativa do cancelamento:',
        buttonConfirm: 'Cancelar compra',
        buttonClose: 'Voltar',
        showTextarea: true,
        textareaLabel: 'Justificativa',
        textareaRequired: true,
        textareaMinLength: 3,
      },
    }).afterClosed().subscribe((result) => {
      if (!result?.confirm) return;
      this.executarCancelamento((result.textareaValue as string).trim());
    });
  }

  private executarCancelamento(justificativa: string): void {
    // Card ainda não persistido — apenas reflete localmente.
    if (!this.card.idCompra) {
      this.card.stage = 'cancelado';
      this.dialogRef.close({ card: this.card, action: 'cancel' });
      return;
    }

    this.cancelando = true;
    // Expande os perfis notificados antes de cancelar: o backend não resolve perfil -> usuários.
    // Pelo Orquestrador: com o toggle da etapa Cancelado, a NF vinculada volta para a gaveta.
    this.automacaoService.idsUsuarioDosPerfis(TipoOcorrenciaCompraEnum.Cancelamento).pipe(
      switchMap(idsUsuarioPerfil =>
        this.orquestradorCompraEtapaService.cancelarCompra(this.card.idCompra!, { justificativa, idsUsuarioPerfil })),
    ).subscribe({
      next: (res) => {
        this.cancelando = false;
        const idsNotasDesvinculadas = res.idsNotasDesvinculadas ?? [];
        // Erro com a etapa preenchida = o cancelamento foi feito e só a marca da NF falhou: aviso, segue fechando.
        const isCanceladaComAviso = res.isError && res.chaveEtapa === 'cancelado';
        if (res.isError && !isCanceladaComAviso) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível cancelar a compra.', AlertType.error);
          return;
        }
        if (isCanceladaComAviso) {
          this.alertService.show('Atenção', res.errorDescription, AlertType.warning);
        } else if (idsNotasDesvinculadas.length > 0) {
          this.alertService.show('Sucesso', idsNotasDesvinculadas.length > 1
            ? `${idsNotasDesvinculadas.length} NF-es liberadas para a gaveta de NFs.`
            : 'NF-e liberada para a gaveta de NFs.', AlertType.success);
        }
        // Cancelou, mas o desvínculo não entrou no histórico de alguma nota.
        if (res.avisoDescricao) this.alertService.show('Atenção', res.avisoDescricao, AlertType.warning);
        this.card.stage = res.chaveEtapa || 'cancelado';
        // O quadro recarrega a gaveta e o mapa das notas quando alguma NF foi liberada.
        this.dialogRef.close({ card: this.card, action: 'cancel', idsNotasDesvinculadas });
      },
      error: () => {
        this.cancelando = false;
        this.alertService.show('Erro', 'Não foi possível cancelar a compra.', AlertType.error);
      },
    });
  }
}
